'use client';

import { useCallback, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import type { VisibilityState } from '@tanstack/react-table';
import { useSettings } from '@/providers/settings-provider';
import { backfillEmployeesBatchAction, backfillWebsiteBatchAction } from '@/lib/actions/backfill';
import type { CompanySearchResult } from '@/lib/actions/company-search';
import { exportToExcel } from './ResultsExportDialog';
import { DEFAULT_COLUMN_VISIBILITY, MOBILE_PAGE_SIZE, SWISS_DEFAULT_COLUMN_VISIBILITY, VISIBLE_COLUMNS_SETTING, VISIBLE_COLUMNS_SETTING_AT, VISIBLE_COLUMNS_SETTING_CH } from './resultsTableColumns';

// CompanyResultsTable's state and actions, one hook per toolbar concern. The
// table has no effects, so these only hold state and memoized callbacks.

export type ProFeature = 'export' | 'columns' | 'enrich';

/** The "Pro feature" prompt and which feature opened it. */
export function useProDialog() {
  const [showProDialog, setShowProDialog] = useState(false);
  // Which feature triggered the Pro prompt — drives the dialog's description.
  const [proDialogFeature, setProDialogFeature] = useState<
    'export' | 'columns' | 'enrich'
  >('export');

  const openProDialog = useCallback(
    (feature: 'export' | 'columns' | 'enrich') => {
      setProDialogFeature(feature);
      setShowProDialog(true);
    },
    [],
  );
  return { showProDialog, setShowProDialog, proDialogFeature, openProDialog };
}

interface ResultsExportInput {
  t: TFunction<'translation', undefined>;
  results: CompanySearchResult[];
  financialYear: number | undefined;
  country: string | undefined;
  canExport: boolean;
  fetchResultsForExport: ((limit: number) => Promise<CompanySearchResult[]>) | undefined;
  onExportClick: (() => void) | undefined;
  onExportSuccess: ((info: { exportCount: number; columns: string[] }) => void) | undefined;
  openProDialog: (feature: ProFeature) => void;
}

/** The Excel export: the dialog's state, the Pro-gated click and the export run. */
export function useResultsExport({
  t,
  results,
  financialYear,
  country,
  canExport,
  fetchResultsForExport,
  onExportClick,
  onExportSuccess,
  openProDialog,
}: ResultsExportInput) {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  const handleExportClick = useCallback(() => {
    // Log every click — including guests and non-entitled users — before the
    // Pro gate, so we can see demand for the export feature.
    onExportClick?.();
    if (!canExport) {
      openProDialog('export');
      return;
    }
    setExportError(false);
    setShowExportDialog(true);
  }, [canExport, onExportClick, openProDialog]);

  const runExport = useCallback(
    async (columnIds: string[], count: number) => {
      setExportError(false);
      setIsExporting(true);
      try {
        // Fall back to the current page if no fetcher was provided.
        const rows = fetchResultsForExport
          ? await fetchResultsForExport(count)
          : results.slice(0, count);
        await exportToExcel(rows, columnIds, t, financialYear, country);
        onExportSuccess?.({ exportCount: rows.length, columns: columnIds });
        setShowExportDialog(false);
      } catch {
        setExportError(true);
      } finally {
        setIsExporting(false);
      }
    },
    [fetchResultsForExport, results, financialYear, t, onExportSuccess, country],
  );
  return { showExportDialog, setShowExportDialog, isExporting, exportError, handleExportClick, runExport };
}

/**
 * Column visibility. Only Pro/Admin may customize columns, so only they get
 * their persisted choice merged over the defaults; everyone else always sees
 * the defaults. `name`/`index` are forced visible so a stale stored value can
 * never hide the anchor columns. Defaults and the stored choice are both
 * per country scope: Swiss (CH) and Austrian (AT) each have their own stored
 * choice, DE and Global share. AT starts from the Swiss defaults — like the
 * Swiss register, the Firmenbuch import carries no financials yet.
 */
export function useColumnVisibility(country: string | undefined, canCustomizeColumns: boolean) {
  const { getOption, storeOption } = useSettings();
  const isSwiss = country === 'CH';
  const isAustrian = country === 'AT';
  const visibilitySetting = isAustrian
    ? VISIBLE_COLUMNS_SETTING_AT
    : isSwiss
      ? VISIBLE_COLUMNS_SETTING_CH
      : VISIBLE_COLUMNS_SETTING;
  const defaultVisibility = isSwiss || isAustrian
    ? SWISS_DEFAULT_COLUMN_VISIBILITY
    : DEFAULT_COLUMN_VISIBILITY;
  const storedVisibility = getOption<VisibilityState | undefined>(
    visibilitySetting,
  );
  const columnVisibility = useMemo<VisibilityState>(() => {
    const base = canCustomizeColumns
      ? { ...defaultVisibility, ...(storedVisibility ?? {}) }
      : { ...defaultVisibility };
    return { ...base, name: true, index: true };
  }, [canCustomizeColumns, storedVisibility, defaultVisibility]);

  const handleColumnVisibilityChange = useCallback(
    (id: string, visible: boolean) => {
      storeOption(visibilitySetting, {
        ...columnVisibility,
        [id]: visible,
      });
    },
    [columnVisibility, storeOption, visibilitySetting],
  );
  return { columnVisibility, handleColumnVisibilityChange };
}

/** Header click → next sort: desc, then asc for toggle fields, else back to revenue-desc. */
export function useSortToggle(onSortChange: ((sort: string) => void) | undefined, sort: string | undefined) {
  return useCallback(
    (field: string) => {
      if (!onSortChange) return;

      const toggleFields = ['profit'];
      let newSort: string;
      if (sort === `${field}-desc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-asc`
          : 'revenue-desc';
      } else if (sort === `${field}-asc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-desc`
          : 'revenue-desc';
      } else {
        newSort = `${field}-desc`;
      }
      onSortChange(newSort);
    },
    [onSortChange, sort],
  );
}

interface ResultsEnrichInput {
  results: CompanySearchResult[];
  canEnrich: boolean;
  openProDialog: (feature: ProFeature) => void;
  onRefresh: (() => void) | undefined;
  onEmployeeCountsUpdated: ((counts: Record<string, number>) => void) | undefined;
}

/** The Pro-gated website and employee enrich batch actions for the page on screen. */
export function useResultsEnrich({
  results,
  canEnrich,
  openProDialog,
  onRefresh,
  onEmployeeCountsUpdated,
}: ResultsEnrichInput) {
  const [isEnrichingWebsites, setIsEnrichingWebsites] = useState(false);
  const [isEnrichingEmployees, setIsEnrichingEmployees] = useState(false);

  const handleEnrichWebsites = useCallback(async () => {
    if (!canEnrich) {
      openProDialog('enrich');
      return;
    }
    const euIds = results.filter((r) => !r.domain).map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingWebsites(true);
    try {
      await backfillWebsiteBatchAction(euIds);
      onRefresh?.();
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingWebsites(false);
    }
  }, [results, canEnrich, openProDialog, onRefresh]);

  const handleEnrichEmployees = useCallback(async () => {
    if (!canEnrich) {
      openProDialog('enrich');
      return;
    }
    const euIds = results.map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingEmployees(true);
    try {
      const res = await backfillEmployeesBatchAction(euIds);
      // Overlay the freshly-parsed counts onto the displayed rows. We don't
      // call onRefresh here — that would re-read the (still stale) mv_search.
      if (res.success && res.results) {
        const counts: Record<string, number> = {};
        for (const r of res.results) {
          if (r.status !== 'ok' || !r.employee_data?.length) continue;
          // Latest year wins, matching how mv_search picks employee_count.
          const latest = r.employee_data
            .filter((d) => d.gesamt != null)
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
          if (latest?.gesamt != null) counts[r.eu_id] = latest.gesamt;
        }
        onEmployeeCountsUpdated?.(counts);
      }
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingEmployees(false);
    }
  }, [results, canEnrich, openProDialog, onEmployeeCountsUpdated]);
  return { isEnrichingWebsites, isEnrichingEmployees, handleEnrichWebsites, handleEnrichEmployees };
}

/** The rows the desktop grid and the mobile card list each show. */
export function useResultSlices(results: CompanySearchResult[], limit: number) {
  // Show up to the current page size (rows-per-page dropdown can raise it
  // above the old fixed 50).
  const displayedResults = useMemo(
    () => results.slice(0, limit),
    [results, limit],
  );
  // Mobile shows 10 cards per page. Slice independently so the brief window
  // before the mobile-limit-override useEffect fires (SSR limit is 50) doesn't
  // flash 50 cards.
  const mobileResults = useMemo(
    () => results.slice(0, MOBILE_PAGE_SIZE),
    [results],
  );
  return { displayedResults, mobileResults };
}
